import { useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { parse as parseQuery, stringify as formatQuery } from 'query-string'
import { actions as renderingActions } from 'builder/modules/rendering'
import { actions as resumeReviewActions } from 'builder/modules/resumeReview'
import { selectors as userModuleSelectors } from 'builder/modules/user/userModule'
import { DocumentTypes as TYPES, FormatType } from 'builder/modules/constants'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'

/** Checks document related query params to start downloading and review flows */
export const useDocumentQueryHandler = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const isResumeDownloaded = useTypedSelector(userModuleSelectors.isResumeDownloaded)

  const {
    download_document_type: type,
    download_document_id: id,
    download_document_format: format,
    download_document_template: template,
    ...rest
  } = useMemo(() => {
    return parseQuery(location.search, { parseNumbers: true })
  }, [location.search])

  // The only param required to download a file is `id`
  const hasValidDocumentParameters = typeof id === 'number'

  useEffect(() => {
    if (hasValidDocumentParameters) {
      // Call rendering engine to generate the file
      dispatch(
        renderingActions.download({
          id,
          type: type as TYPES,
          format: format as FormatType,
          template: template as string,
        }),
      )
      // Send resume to review
      if (type !== TYPES.coverLetter) dispatch(resumeReviewActions.sendResume({ resumeId: id }))
    }
  }, [dispatch, format, hasValidDocumentParameters, id, template, type])

  useEffect(() => {
    // Clean the URL only after download of resume is done
    if (isResumeDownloaded && hasValidDocumentParameters) {
      navigate(`${location.pathname}?${formatQuery(rest)}`, { replace: true })
    }
  }, [isResumeDownloaded, rest, hasValidDocumentParameters, navigate, location.pathname])
}
